import React, { useCallback } from 'react';
import * as fabric from 'fabric';
import { A4_WIDTH, A4_HEIGHT, PAGE_MARGIN } from '../constants';

export type AlignmentType = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom';
export type DistributionType = 'horizontal' | 'vertical';

export function useCanvasAlignment(
  fabricCanvasRef: React.RefObject<fabric.Canvas | null>,
  saveHistoryImmediate: (canvas?: fabric.Canvas) => void
) {
  const alignSelected = useCallback((alignment: AlignmentType) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    if (activeObject.type === 'activeSelection') {
      const sel = activeObject as fabric.ActiveSelection;
      const groupBounds = sel.getBoundingRect();
      const groupObjects = sel.getObjects();

      groupObjects.forEach((obj: fabric.FabricObject) => {
        const objBounds = obj.getBoundingRect();
        switch (alignment) {
          case 'left':
            obj.set({ left: groupBounds.left });
            break;
          case 'center-h':
            obj.set({ left: groupBounds.left + (groupBounds.width - objBounds.width) / 2 });
            break;
          case 'right':
            obj.set({ left: groupBounds.left + groupBounds.width - objBounds.width });
            break;
          case 'top':
            obj.set({ top: groupBounds.top });
            break;
          case 'center-v':
            obj.set({ top: groupBounds.top + (groupBounds.height - objBounds.height) / 2 });
            break;
          case 'bottom':
            obj.set({ top: groupBounds.top + groupBounds.height - objBounds.height });
            break;
        }
        if (obj.setCoords) obj.setCoords();
      });
    } else {
      const objBounds = activeObject.getBoundingRect();
      switch (alignment) {
        case 'left':
          activeObject.set({ left: PAGE_MARGIN });
          break;
        case 'center-h':
          activeObject.set({ left: (A4_WIDTH - objBounds.width) / 2 });
          break;
        case 'right':
          activeObject.set({ left: A4_WIDTH - PAGE_MARGIN - objBounds.width });
          break;
        case 'top':
          activeObject.set({ top: PAGE_MARGIN });
          break;
        case 'center-v':
          activeObject.set({ top: (A4_HEIGHT - objBounds.height) / 2 });
          break;
        case 'bottom':
          activeObject.set({ top: A4_HEIGHT - PAGE_MARGIN - objBounds.height });
          break;
      }
      if (activeObject.setCoords) activeObject.setCoords();
    }

    canvas.renderAll();
    saveHistoryImmediate(canvas);
  }, [fabricCanvasRef, saveHistoryImmediate]);

  const distributeSelected = useCallback((direction: DistributionType) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== 'activeSelection') return;

    const sel = activeObject as fabric.ActiveSelection;
    const groupObjects = sel.getObjects();
    if (groupObjects.length < 3) return;

    if (direction === 'horizontal') {
      groupObjects.sort((a, b) => (a.left || 0) - (b.left || 0));
      const first = groupObjects[0];
      const last = groupObjects[groupObjects.length - 1];
      const step = ((last.left || 0) - (first.left || 0)) / (groupObjects.length - 1);

      groupObjects.forEach((obj, idx) => {
        if (idx !== 0 && idx !== groupObjects.length - 1) {
          obj.set({ left: (first.left || 0) + step * idx });
          if (obj.setCoords) obj.setCoords();
        }
      });
    } else {
      groupObjects.sort((a, b) => (a.top || 0) - (b.top || 0));
      const first = groupObjects[0];
      const last = groupObjects[groupObjects.length - 1];
      const step = ((last.top || 0) - (first.top || 0)) / (groupObjects.length - 1);

      groupObjects.forEach((obj, idx) => {
        if (idx !== 0 && idx !== groupObjects.length - 1) {
          obj.set({ top: (first.top || 0) + step * idx });
          if (obj.setCoords) obj.setCoords();
        }
      });
    }

    canvas.renderAll();
    saveHistoryImmediate(canvas);
  }, [fabricCanvasRef, saveHistoryImmediate]);

  return {
    alignSelected,
    distributeSelected,
  };
}
